import { useEffect, useState, useRef } from "react";
import { useQueryClient } from "react-query";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { ReactComponent as Download01 } from "../../../assets/icons/download-01.svg";
import { APP_TITLE } from "../../../config/constant";
import { defaultStatusColors } from "../../../utils/CardUtils";
import { formatDateByFormat } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import InfinityGridBillingInvoicesCard from "../../common/cardContainers/InfinityGridBillingInvoicesCard";
import ListComponent from "../../common/lists/ListComponent";
import MainWindowHeader from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function BillingInvoicesPage() {
    document.title = `${APP_TITLE} - Billing Invoices`;

    // #region STATES
    const { MainLayoutProps, layoutType } = useOutletContext();
    const queryClient = useQueryClient();
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const [listNumRows, setListNumRows] = useState(50);
    const [requestEnabled, setRequestEnabled] = useState(false);
    const gridRef = useRef(null);

    //----- Header Filters
    const [searchText, setSearchText] = useState("");
    const [allCompaniesList, setAllCompaniesList] = useState([]);
    const [filteredCompany, setFilteredCompany] = useState([]);
    //-----
    const [requestsEnabled, setRequestsEnabled] = useState(false);
    //-----
    const filters = {
        filterCompanyIds: filteredCompany?.map(item => item.value),
        searchText: searchText
    };

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const infiniteGridQuerykey = ["BillingInvoicesGrid", filters];
    const listQueryKey = ["BillingInvoicesList", filters];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);
        setupHeaders(
            props,
            onClickRefreshAction,
            onSearch,
            allCompaniesList,
            onCompanyChange,
            filteredCompany,
            onFiltersLoaded
        );
    }, [props?.layoutType, allCompaniesList, filteredCompany, requestsEnabled]);

    useEffect(() => {
        Library.removeQueries(queryClient, infiniteGridQuerykey);
        Library.removeQueries(queryClient, listQueryKey);
    }, []);

    useEffect(() => {
        setRequestEnabled(true);
    }, [filters]);
    // #endregion

    // #region LIST PROPS
    const listColumns = [
        {
            name: "Amount",
            selector: row => (row?.amount !== undefined ? `${row?.amount} ${row?.currency}` : "--")
        },
        {
            name: "Number",
            selector: row => row?.id ?? "--"
        },
        {
            name: "For Period",
            selector: row => {
                const periodStart = row?.periodStart ? formatDateByFormat(row?.periodStart, "MMM DD") : undefined;
                const periodEnd = row?.periodEnd ? formatDateByFormat(row?.periodEnd, "MMM DD") : undefined;
                const period = periodStart && periodEnd ? `${periodStart} - ${periodEnd}` : "--";
                const dueDate = row?.periodEnd ? formatDateByFormat(row?.periodEnd, "MMM DD - HH:mm") : "--";

                return (
                    <span>
                        <strong>Due: {dueDate ? dueDate : "--"}</strong>
                        <br />
                        {period ? period : "--"}
                    </span>
                );
            }
        },
        {
            name: "Payments",
            selector: row => (
                <span
                    className={
                        row?.paymentURL && row?.status !== "Paid"
                            ? "underline cursor-pointer"
                            : "text-inactive cursor-not-allowed"
                    }
                    onClick={() => {
                        if (row?.paymentURL) Library.openNewPage(row?.paymentURL);
                    }}>
                    payment
                </span>
            )
        },
        {
            name: "Document",
            selector: row => (
                <div className={`flex items-center gap-4px ${row?.downloadPDF ? "" : "text-inactive"}`}>
                    <Download01 className="mr-2" />

                    <span
                        className={row?.downloadPDF ? "underline cursor-pointer" : "cursor-not-allowed"}
                        onClick={() => {
                            if (row?.downloadPDF) Library.openNewPage(row?.downloadPDF);
                        }}>
                        document
                    </span>
                </div>
            )
        }
    ];
    // #endregion

    // #region METHODS
    const onSearch = searchText => {
        setSearchText(searchText);
    };

    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
            gridRef.current?.reset?.(infiniteGridQuerykey);
        }
    };

    const onCompanyChange = selection => {
        if (!selection?.locked) {
            setFilteredCompany([selection]);
        }
    };

    const onFiltersLoaded = data => {
        if (data?.companies?.length) {
            const companies = data?.companies?.map(item => {
                const defaultCompanyID = localStorage.getItem("currentCompanyID");
                let companyName = item?.name;

                if (item?._id === defaultCompanyID) companyName = `${item?.name} (default)`;

                return {
                    name: companyName,
                    value: item?._id,
                    selected: item?._id === defaultCompanyID
                };
            });

            setAllCompaniesList(companies);
            setFilteredCompany([companies.find(item => item?.selected)]);
        }

        setRequestsEnabled(true);
    };
    // #endregion

    // #region COMPUTED PROPERTIES
    const listApi = {
        endpoint: "getInvoices",
        useCommonPostRequest: true,
        commonContext: "Billing",

        formData: {
            companyID: localStorage.getItem("currentCompanyID"),
            limit: listNumRows,
            ...filters
        },
        onSuccess: data => {
            const res = data?.result?.map(item => {
                let status = item.status || "--";
                status = status.charAt(0).toUpperCase() + status.slice(1); // Capitalize the first letter

                if (status === "Draft") status = "New";
                item.status = status;
                return item;
            });

            return res;
        }
    };

    const listActions = {
        includeActions: true,
        extraActions: [
            {
                title: "View"
            }
            /*{
                title: "Download"
            },
            {
                title: "Pay"
            }*/
        ],
        editIsVisible: false,
        removeIsVisible: false,
        suspendIsVisible: false,
        viewIsVisible: false
    };

    const listStatus = {
        stickyStatus: true,
        statusFromPath: "status",
        statusColors: defaultStatusColors
    };
    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            {viewLayoutStyle === "Grid" && (
                <InfinityGridBillingInvoicesCard
                    ref={gridRef}
                    minColumnWidth="250px"
                    limit="8"
                    queryKey={infiniteGridQuerykey}
                    statusFromPath="status"
                    filters={filters}
                    requestEnabled={requestEnabled}
                />
            )}

            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="Invoices"
                    columns={listColumns}
                    api={listApi}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={listActions}
                    includeStatus={listStatus}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    requestsEnabled={requestsEnabled}
                />
            )}
        </StyledPage>
    );
    // #endregion
}

// #region SMALL COMPONENTS
function setupHeaders(
    props,
    onClickRefreshAction,
    onSearch,
    allCompaniesList,
    onCompanyChange,
    filteredCompany,
    onFiltersLoaded
) {
    props.setControlBarLeftBtns([]);
    props.setControlBarCenterBtns([{ to: "/billing", active: true, disabled: false, title: "BILLING" }]);
    props.setControlBarRightBtns([
        { to: "/billing", active: false, disabled: false, title: "Plans", icon: "file-check-01" },
        { to: "/billing/invoices", active: true, disabled: false, title: "Invoices", icon: "file-check-02" },
        { to: "/billing/reports", active: false, disabled: true, title: "Subs", icon: "file-03" },
        { to: "/billing/methods", active: false, disabled: false, title: "Methods", icon: "credit-card-check" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allCompaniesList,
                    onDropdownChange: onCompanyChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: filteredCompany?.[0]?.name
                }}
                secondOrganize={{
                    visible: true,
                    disabled: true
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useCompanies: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    searchPlaceholder: "Search",
                    onSearch: onSearch
                }}
            />
        )
    });
}
// #endregion
